import { Box, Paper, Typography, useTheme } from '@mui/material';
import { formatMetricValue } from '../../utils/formatMetricValue';
import StatusBadge from './StatusBadge';
import TrendBadge from './TrendBadge';
import TileMetaActions from '../TileMetaActions';
import { cardShellSx, getMetricDisplayName } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import { tileMetaTitlePad } from '../../utils/tileMeta';

/** Design §4 statCard: value, description, trend */
export default function StatCardRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.4, mb: 1, pr: tileMetaTitlePad(data.meta) }}
        >
          {title}
        </Typography>
      ) : null}
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.15, mb: 1.5 }}>
        {displayValue}
      </Typography>
      <Box sx={{ mt: 'auto' }}>
        {data.trend ? (
          <TrendBadge trend={data.trend} />
        ) : data.description ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {data.description}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

/** Design §4 statCardWithBadge: value, description, badge */
export function StatCardWithBadgeRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          pr: tileMetaTitlePad(data.meta),
        }}
      >
        {title ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
            {title}
          </Typography>
        ) : (
          <Box />
        )}
        {data.badge ? <StatusBadge badge={data.badge} /> : null}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 500 }}
      >
        Leads under Nurturing
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.15, mb: 1.5 }}>
        {displayValue}
      </Typography>
      {data.description ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5, mt: 'auto' }}>
          {data.description}
        </Typography>
      ) : null}
    </Paper>
  );
}

/** Design §4 statCardWithDot: value + breakdown[0].color as legend dot */
export function StatCardWithDotRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);
  const colorToken = data.breakdown?.[0]?.color;
  const dotColor =
    colorToken === 'warning'
      ? theme.palette.warning.main
      : colorToken === 'grey'
        ? theme.palette.grey[500]
        : theme.palette.primary.main;

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, pr: tileMetaTitlePad(data.meta) }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
        {title ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
            {title}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2, mb: 0.5 }}>
        {displayValue}
      </Typography>
      {data.breakdown?.[0]?.pct != null ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 'auto' }}>
          {data.breakdown[0].pct}% of cohort
        </Typography>
      ) : null}
    </Paper>
  );
}
